import {autobind} from 'core-decorators';

import {Component} from 'inferno'; // eslint-disable-line @typescript-eslint/no-unused-vars

import {Page, Link} from '../../common/components';
import {isStatic, isSticker} from '../../common/util';
import emitter from '../../common/util/emitter';

import {UniversalGifList} from '../components/UniversalGifList';
import {Carousel} from '../components/Carousel';


export class BaseProfilePage extends Page {
    static PROFILE = 'profile';
    static FAVORITES = 'favorites';
    static STICKERS = 'stickers';
    static MEMES = 'memes';

    profilePageView?: boolean;
    memePageView?: boolean;
    stickerPageView?: boolean;
    favoritesPageView?: boolean;
    showShareCount?: boolean;

    pageChanged(nextProps: jsx.Props) {
        return nextProps.username !== this.props.username ||
            nextProps.section !== this.props.section;
    }
    propsNeedProcessing(nextProps: jsx.Props) {
        for (const key of ['profile', 'gifs', 'memes', 'stickers']) {
            if (nextProps[key] !== this.props[key]) {
                return true;
            }
        }
        return false;
    }
    pageInit(props: jsx.Props) {
        this.ttl = this.context.CONSTANTS.PROFILE_PAGE_TTL;

        this.title = '';
        this.description = '';
        this.h1_title = props.username;
    }
    processNewProps(props: jsx.Props) {
        this.showShareCount = process.env.BROWSER && props.ownsProfile;
        this.handleProfileBody(props);
        this.done(props);
    }
    componentWillMount() {
        this.context.uploadService.clearProcessed();
    }
    componentDidMount() {
        emitter.on('uploads-processed', this.triggerUpdate);
    }
    componentWillUnmount() {
        emitter.off('uploads-processed', this.triggerUpdate);
    }
    @autobind
    trackSeeAllContentTap(mediaType: string) {
        return () => {
            this.context.apiService.trackSeeAllContentTap(mediaType, 'profile');
        };
    }
    handleProfileBody(props: jsx.Props) {
        const gettextSub = this.context.gtService.gettextSub;
        const body = props.profile;
        const user = body.user;

        if (!body.pending) {
            if (!user || user.userid === '-1') {
                this.context.response.status = 404;
                return;
            }

            this.setCanonicalURL(
                this.linkToProfile(user, props.mediatype));
            this.title = gettextSub('{username}\'s GIFs on Tenor', {
                username: user.username});
            this.description = gettextSub('Check out {username}\'s GIFs on Tenor. Discover, search and share popular GIFs with friends on Tenor.', {
                username: this.props.profile.user.username,
            });
        } else {
            this.context.response.status = 200;
        }
    }
    done(props: jsx.Props) {
        const uploadService = this.context.uploadService;
        this.debug('done', {
            fetchingInProgress: uploadService.fetchingInProgress,
            username: this.props.profile.user.username,
            ownsProfile: props.ownsProfile,
        });
        if (!uploadService.fetchingInProgress &&
            props.ownsProfile === true) {
            console.log('upload fetch in progress');
            uploadService.fetchUnprocessed();
        }
    }
    @autobind
    handlePageDone() {
        super.handlePageDone();
        if (process.env.BROWSER) {
            const usertype = this.props.profile.user.usertype;
            const query: {partner_profile: string, usertype?: string} = {
                partner_profile: usertype === 'user' ?
                    '' :
                    this.props.profile.user.username,
            };
            if (usertype) {
                query.usertype = usertype;
            }
            this.context.apiService.registerEvent('profile_page_view', query);
        }
    }
    @autobind
    getMoreGifs() {
        this.context.store.call(
            'api.gifs.searchByUsername.*',
            [this.props.username, this.props.section, this.props.ownsProfile],
            'more',
        );
    }
    @autobind
    getMoreMemes() {
        this.context.store.call(
            'api.memes.searchByUsername.*',
            [this.props.username, this.props.section, this.props.ownsProfile],
            'more',
        );
    }
    @autobind
    getMoreStickers() {
        this.context.store.call(
            'api.stickers.searchByUsername.*',
            [this.props.username, this.props.section, this.props.ownsProfile],
            'more',
        );
    }
    @autobind
    setSection(section: string) {
        const newVal = {
            ...this.context.store.set('ui.BaseProfilePage.*', [this.props.username]),
            section,
        };
        this.context.store.set('ui.BaseProfilePage.*', [this.props.username], newVal);
    }

    prepareItemLists(): [api.Post[], api.Post[], api.Post[]] {
        const uploadService = this.context.uploadService;
        let gifList: api.Post[] = [...this.props.gifs.results];
        let memeList: api.Post[] = [...this.props.memes.results];
        let stickerList: api.Post[] = [...this.props.stickers.results];
        const unprocessedGifs: api.Post[] = [];
        let processedGifs: api.Post[] = [];
        const processedStickers: api.Post[] = [];
        const processedMemes: api.Post[] = [];
        this.debug('prepareItemLists', memeList, this.props.memes.results);

        if (this.props.ownsProfile === true) {
            for (const id in uploadService.unprocessedUploads) {
                if (gifList.some((gif) => gif.id === id)) {
                    uploadService.removeUnprocessedUpload(id);
                } else {
                    unprocessedGifs.push(uploadService.unprocessedUploads[id]);
                }
            }
            for (const id in uploadService.processedUploads) {
                if (gifList.some((gif) => gif.id === id)) {
                    uploadService.removeProcessedUpload(id);
                } else {
                    processedGifs.push(uploadService.processedUploads[id]);
                }
            }
        }
        if (this.props.section === BaseProfilePage.PROFILE) {
            processedGifs = processedGifs.filter((gif) => {
                const staticItem = isStatic(gif);
                const sticker = isSticker(gif);
                if (staticItem) {
                    processedMemes.push(gif);
                } else if (sticker) {
                    processedStickers.push(gif);
                }
                return !staticItem && !sticker;
            });
            memeList = [...processedMemes, ...memeList];
            stickerList = [...processedStickers, ...stickerList];
            gifList = [...unprocessedGifs, ...processedGifs, ...gifList];
        }
        if (unprocessedGifs.length && !uploadService.fetchingInProgress) {
            uploadService.fetchUnprocessed();
        }
        return [gifList, memeList, stickerList];
    }
    renderGifListSection(gifList: api.Post[]): Element {
        const gettextSub = this.context.gtService.gettextSub;
        const {usertype, username, partnername} = this.props.profile.user;
        const isPartner = usertype === 'partner';
        const authStatusDetermined = !this.props.auth.pending;

        let header;
        if (this.profilePageView || this.stickerPageView || this.memePageView) {
            header = `${isPartner ? partnername : username} GIFs`;
        } else if (this.favoritesPageView) {
            header = gettextSub('Favorites');
        }

        return (
            <div className="media-section">
                <div className="media-section-header">
                    <h2>{header}</h2>
                </div>
                {authStatusDetermined && <UniversalGifList
                    gifs={gifList}
                    loaded={this.props.gifs.loaded}
                    pending={this.props.gifs.pending}
                    itemsExhaustedCallback={this.getMoreGifs}
                    showShareCount={this.showShareCount}
                />}
            </div>
        );
    }
    renderMemeListSection(memeList: api.Post[]): Element {
        const gettextSub = this.context.gtService.gettextSub;
        const {usertype, username, partnername} = this.props.profile.user;
        const isPartner = usertype === 'partner';
        const header = `${isPartner ? partnername : username} ${gettextSub('Memes')}`;
        const itemsData = this.props.memes;
        const hasItems = memeList.length > 0;
        const authStatusDetermined = !this.props.auth.pending;

        return (
            <div className="media-section">
                <div className="media-section-header">
                    <h2>{header}</h2>
                    { this.memePageView &&
                        <Link onClick={this.trackSeeAllContentTap('gifs')} to={
                            this.linkToProfile(this.props.profile.user)}
                        >
                            {gettextSub('See GIFs')}
                        </Link>
                    }
                    { !this.memePageView &&
                        <Link onClick={this.trackSeeAllContentTap('memes')} to={
                            this.linkToProfile(this.props.profile.user, 'memes')}
                        >
                            {gettextSub('See all')}
                        </Link>
                    }
                </div>
                { authStatusDetermined && hasItems && this.memePageView &&
                    <UniversalGifList
                        className='memes'
                        gifs={memeList}
                        loaded={itemsData.loaded}
                        pending={itemsData.pending}
                        itemsExhaustedCallback={this.getMoreMemes}
                        showShareCount={this.showShareCount}
                    />
                }
                { authStatusDetermined && hasItems && this.profilePageView &&
                    this.props.isMobile && <UniversalGifList
                    className='memes'
                    gifs={memeList.slice(0, 6)}
                    loaded={itemsData.loaded}
                    pending={itemsData.pending}
                    staticColumns={3}
                    showShareCount={this.showShareCount}
                /> }
                { authStatusDetermined && hasItems && this.profilePageView &&
                    !this.props.isMobile && <Carousel
                    items={memeList}
                    type={'items'}
                    itemsExhaustedCallback={this.getMoreMemes}
                    showShareCount={this.showShareCount}
                /> }
            </div>
        );
    }
    renderStickerListSection(stickerList: api.Post[]): Element {
        const gettextSub = this.context.gtService.gettextSub;
        const {usertype, username, partnername} = this.props.profile.user;
        const isPartner = usertype === 'partner';
        const header = `${isPartner ? partnername : username} ${gettextSub('Stickers')}`;
        const itemsData = this.props.stickers;
        const hasItems = stickerList.length > 0;
        const authStatusDetermined = !this.props.auth.pending;

        return (
            <div className="media-section">
                <div className="media-section-header">
                    <h2>{header}</h2>
                    { this.stickerPageView &&
                        <Link
                            onClick={this.trackSeeAllContentTap('gifs')}
                            to={this.linkToProfile(this.props.profile.user)}
                        >
                            {gettextSub('See GIFs')}
                        </Link>
                    }
                    { !this.stickerPageView &&
                        <Link
                            onClick={this.trackSeeAllContentTap('stickers')}
                            to={this.linkToProfile(this.props.profile.user, 'stickers')}
                        >
                            {gettextSub('See all')}
                        </Link>
                    }
                </div>
                { authStatusDetermined && hasItems && this.stickerPageView &&
                    <UniversalGifList
                        className='stickers'
                        gifs={stickerList}
                        loaded={itemsData.loaded}
                        pending={itemsData.pending}
                        itemsExhaustedCallback={this.getMoreStickers}
                        showShareCount={this.showShareCount}
                    />
                }
                { authStatusDetermined && hasItems && this.profilePageView &&
                        this.props.isMobile && <UniversalGifList
                    className='stickers'
                    gifs={stickerList.slice(0, 6)}
                    loaded={itemsData.loaded}
                    pending={itemsData.pending}
                    staticColumns={3}
                    showShareCount={this.showShareCount}
                /> }
                { authStatusDetermined && hasItems && this.profilePageView &&
                        !this.props.isMobile && <Carousel
                    items={stickerList}
                    type={'items'}
                    itemsExhaustedCallback={this.getMoreStickers}
                    showShareCount={this.showShareCount}
                /> }
            </div>
        );
    }
    renderFavoritesPageView(
        ownsProfile: boolean, gifList: api.Post[]): Element {
        const gettextSub = this.context.gtService.gettextSub;
        return <div className="content">
            { ownsProfile === true &&
                <div className="switch-view-link">
                    <a onClick={() => {
                        this.setSection(BaseProfilePage.PROFILE);
                    }}>
                        {gettextSub('View Uploads')}
                    </a>
                </div>
            }
            { this.renderGifListSection(gifList) }
        </div>;
    }
    renderMemePageView(
        isMobile: boolean,
        memeList: api.Post[],
        gifList: api.Post[]): Element {
        return <div className="content">
            { this.renderMemeListSection(memeList) }
            { isMobile && this.renderGifListSection(gifList) }
        </div>;
    }
    renderStickerPageView(
        isMobile: boolean,
        stickerList: api.Post[],
        gifList: api.Post[]): Element {
        return <div className="content">
            { this.renderStickerListSection(stickerList) }
            { isMobile && this.renderGifListSection(gifList) }
        </div>;
    }
}
